import Send from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { alpha } from '@mui/material/styles';
import { type FC, type KeyboardEvent, useState } from 'react';
import { NAVY, RADIUS_COIN, SURFACE_CONTAINER_LOW, YELLOW } from 'styles/tokens';

interface ComposerProps {
  onSend: (text: string) => void;
  sending: boolean;
  disabled?: boolean;
}

/**
 * Live-chat composer: type a user message, send it, and the model replies into the thread.
 * Enter sends, Shift+Enter inserts a newline (pattern lifted from the Demochat composer).
 */
const Composer: FC<ComposerProps> = ({ onSend, sending, disabled = false }) => {
  const [text, setText] = useState('');

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || sending || disabled) {
      return;
    }
    onSend(trimmed);
    setText('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, pt: 1, flexShrink: 0 }}>
      <InputBase
        multiline
        maxRows={6}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Escribe como el cliente… (Enter envía, Shift+Enter salto de línea)"
        disabled={disabled}
        fullWidth
        sx={{
          bgcolor: SURFACE_CONTAINER_LOW,
          borderRadius: RADIUS_COIN,
          px: 1.75,
          py: 1,
          fontFamily: 'Jost, sans-serif',
          fontSize: 13.5,
          color: NAVY,
          '& textarea::placeholder': { color: alpha(NAVY, 0.4), opacity: 1 },
        }}
      />
      <IconButton
        onClick={submit}
        disabled={!text.trim() || sending || disabled}
        sx={{
          bgcolor: YELLOW,
          color: NAVY,
          width: 40,
          height: 40,
          flexShrink: 0,
          '&:hover': { bgcolor: YELLOW, transform: 'translateY(-1px)' },
          '&.Mui-disabled': { bgcolor: alpha(NAVY, 0.06), color: alpha(NAVY, 0.3) },
        }}
      >
        {sending ? <CircularProgress size={18} sx={{ color: NAVY }} /> : <Send sx={{ fontSize: 18 }} />}
      </IconButton>
    </Box>
  );
};

export default Composer;
